import { ENV_DEV_OR_STAGING } from '@/helpers/environment'
import { FUNNELS } from '@/data/funnels'

const DOMAINS = {
	'simpleapp.one': {
		SIMPLEAPP: true
	},
	'yt.simple-life-app.com': {
		YOUTUBE: true
	},
	'activity.simple-life-app.com': {
		ACTIVITY: true
	},
	localhost: {
		LOCALHOST: true
	},
	default: {
		DEFAULT: true
	}
}

/**
 * Method for extracting data from `URLSearchParams` entries to plain object
 * Supports multiple params with the same name
 * @param {iterable} entries - iterable collection of tuples, i.e. `[['exp', 'foo'], ['exp', 'bar'], ['a', 'b']]`
 * @returns {object} - result object, i.e `{ exp: ['foo', 'bar'], a: 'b' }`
 */
function groupSearchParamsByKey(entries) {
	return [...entries].reduce((result, tuple) => {
		const [key, val] = tuple
		if (result.hasOwnProperty(key)) {
			if (Array.isArray(result[key])) {
				result[key] = [...result[key], val]
			} else {
				result[key] = [result[key], val]
			}
		} else {
			result[key] = val
		}

		return result
	}, {})
}

export default {
	namespaced: true,

	state: {
		host: window.location.hostname,
		queryParams: {},
		funnelType: null
	},

	mutations: {
		storeQueryParams(state) {
			const urlSearchParams = new URLSearchParams(window.location.search)
			const params = groupSearchParamsByKey(urlSearchParams.entries())
			const normalizedParams = {}

			for (let param in params) {
				let paramValue = params[param]

				if (paramValue === '' || paramValue === 'true') {
					paramValue = true
				} else if (paramValue === 'false') {
					paramValue = false
				}

				normalizedParams[param] = paramValue
			}

			state.queryParams = {
				...state.queryParams,
				...normalizedParams
			}
		},

		setFunnelType(state, { funnelType }) {
			state.funnelType = funnelType
		}
	},

	getters: {
		hostname: (state) => state.host,

		domain: (state, getters) => {
			let host = state.host

			if (ENV_DEV_OR_STAGING) {
				host = getters.getQueryParam('host') ?? state.host
			}

			return DOMAINS[host] ?? DOMAINS.default
		},

		getQueryParam: (state) => (parameter) => state.queryParams[parameter],

		isPaidTraffic: (state, getters) => Boolean(getters.getQueryParam('utm_source') || getters.isReferral),

		manageSubscriptionSource: (state) => state.queryParams.manage_subscription_source ?? 'web',

		isAppToWebToAppScenario: (state) => state.queryParams.login_source === 'android',

		getFunnelType: (state) => {
			return state.funnelType || FUNNELS.MAIN
		},

		isReferral: (state, getters) => {
			return ['referral_ios_v1', 'referral_ios_v2', 'referral_web_v2'].includes(getters.getQueryParam('from'))
		},

		getReferralCode: (state, getters) => {
			if (getters.isReferral) {
				return getters.getQueryParam('refcode')
			}
			return null
		}
	},
	actions: {
		/*
			Method for checking if user came from organic traffic
			We are setting local flag to use it in strapi/in code to show changes based on it
			Its being set only once on app initialisation
			Only 4 cases we defined as "organic"
			https://simple-life-app.com/ is organic – w/o any utm sources
			https://simple-life-app.com/?utm_source=simple_landing – from simple.life main page
			https://simple-life-app.com/?utm_source=nb_blog - from simple.life blog
			https://simple-life-app.com/?utm_source=cta_blog - from simple.life blog
			Anything else is not considered "organic"
			https://simple-life-app.com/?utm_source=FB – is NOT organic, it's from ads on facebook
			https://simple-life-app.com/?utm_source=google – is NOT organic, it's from ads on google
			etc...
		 */
		defineOrganicTrafficFlag({ getters, commit }) {
			const utmSource = getters['getQueryParam']('utm_source')
			const manageSubscriptionSource = getters['getQueryParam']('manage_subscription_source')
			let isOrganicUtmSource = false

			if (!utmSource || ['simple_landing', 'nb_blog', 'cta_blog'].some((v) => utmSource.includes(v))) {
				isOrganicUtmSource = true
			}

			if (manageSubscriptionSource) {
				isOrganicUtmSource = false
			}
			commit('userFlags/updateLocalFlags', { organicTrafficOnboarding: isOrganicUtmSource }, { root: true })
		}
	}
}
